import React, {forwardRef, useEffect} from 'react';
import {DEVICE_PARAM_TYPE} from '../../../features/Checkout/constants';

export interface ExpressCheckoutErrorEvent extends CustomEvent {
  detail:
    | string
    | {
        url: string;
        statusCode: number;
        errorKey: string;
        message: string;
      };
}

export interface ExpressCheckoutFinishedEvent extends CustomEvent {
  detail: {
    id: string;
    isFinalized: boolean;
    orderId: number;
  };
}

export interface InvalidAccessTokenEvent extends CustomEvent {
  detail: {
    paymentKey: string;
    provider: string;
  };
}

interface ExpressCheckoutEvents {
  'consent-missing': CustomEvent;
  'invalid-access-token': CustomEvent;
  'express-checkout-finished': ExpressCheckoutFinishedEvent;
  'payment-closed': CustomEvent;
  'express-error': ExpressCheckoutErrorEvent;
  'payment-start': CustomEvent;
}

export type ExpressCheckoutElement = HTMLDivElement & {
  addEventListener<K extends keyof ExpressCheckoutEvents>(
    type: K,
    listener: (ev: ExpressCheckoutEvents[K]) => void,
  ): void;
  removeEventListener<K extends keyof ExpressCheckoutEvents>(
    type: K,
    listener: (ev: ExpressCheckoutEvents[K]) => void,
  ): void;
};

interface RawExpressCheckoutComponentProps {
  'data-testid'?: string;
  device: DEVICE_PARAM_TYPE | 'web';
  jwt?: string;
  accessToken: string;
  basketTotal?: number;
  expressDirectEntry?: {paymentKey: string; provider: string};
  onInvalidAccessToken: (event: CustomEvent) => void;
  onExpressCheckoutFinished: (event: CustomEvent) => void;
  onPaymentClosed: () => void;
  onExpressError: (event: CustomEvent) => void;
  onPaymentStart?: () => void;
}

export const RawExpressCheckoutComponent = forwardRef<
  ExpressCheckoutElement,
  RawExpressCheckoutComponentProps
>(
  (
    {
      'data-testid': dataTestId,
      device,
      jwt,
      accessToken,
      basketTotal,
      expressDirectEntry,
      onInvalidAccessToken,
      onExpressCheckoutFinished,
      onPaymentClosed,
      onExpressError,
      onPaymentStart,
    }: RawExpressCheckoutComponentProps,
    ref,
  ) => {
    // eslint-disable-next-line local-rules/too-complex-useeffects
    useEffect(() => {
      const element =
        ref as React.MutableRefObject<ExpressCheckoutElement | null>;

      if (element.current) {
        element.current.addEventListener(
          'invalid-access-token',
          onInvalidAccessToken,
        );
        element.current.addEventListener(
          'express-checkout-finished',
          onExpressCheckoutFinished,
        );
        element.current.addEventListener('payment-closed', onPaymentClosed);
        element.current.addEventListener('express-error', onExpressError);
        if (onPaymentStart) {
          element.current.addEventListener('payment-start', onPaymentStart);
        }
      }

      return () => {
        if (element.current) {
          element.current.removeEventListener(
            'invalid-access-token',
            onInvalidAccessToken,
          );
          element.current.removeEventListener(
            'express-checkout-finished',
            onExpressCheckoutFinished,
          );
          element.current.removeEventListener(
            'payment-closed',
            onPaymentClosed,
          );
          element.current.removeEventListener('express-error', onExpressError);
          if (onPaymentStart) {
            element.current.removeEventListener(
              'payment-start',
              onPaymentStart,
            );
          }
        }
      };
    }, [
      onInvalidAccessToken,
      onExpressCheckoutFinished,
      onPaymentClosed,
      onExpressError,
      onPaymentStart,
      ref,
    ]);

    return (
      <scayle-express-checkout
        ref={ref as React.RefObject<HTMLDivElement | null>}
        data-testid={dataTestId}
        device={device === 'web' ? 'web' : 'app'}
        jwt={jwt}
        access-token={accessToken}
        consent='{"paypal":true,"klarna":true}'
        basket-total={basketTotal}
        express-direct-entry={JSON.stringify(expressDirectEntry)}
        // debug={true} // uncomment for testing purposes
      />
    );
  },
);

RawExpressCheckoutComponent.displayName = 'RawExpressCheckoutComponent';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      ['scayle-express-checkout']: {
        key?: string;
        style?: object;
        ref?: React.RefObject<HTMLDivElement | null>;
        'data-testid'?: string;
        consent: string;
        device: 'web' | 'app';
        'access-token': string;
        'basket-total'?: number;
        jwt?: string;
        'express-direct-entry'?: string; // {paymentKey: string; provider: string};
        // debug?: boolean;
      };
    }
  }
}
